import { Menu, Modal } from '@mantine/core';
import PrimaryDropDown from 'app/views/components/DropDown/DropDown';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteEntity,
  activeEntity,
  makeInactiveEntity,
} from './entity-daml-actions';
import { EntityParams } from 'app/models/entity-params';
import { getEntities } from 'app/state/ducks/entities/selectors';
import { loadOneEntity } from 'app/state/ducks/entities/thunks';
import { loadEntityEditLists } from 'app/state/utils/form-lists';
import { ErrorNotification } from 'app/views/components/notifications/notification';
import { useNavigate } from 'react-router-dom';
import { ContactsModal } from './entity-contacts/contacts-modal';
import IonIcon from '@reacticons/ionicons';
import MultiPartForm from 'app/views/pages/accounts-management/entities/multipart-form-modal';
import { ContactParams } from 'app/models/contact-params';
import { getContacts } from 'app/state/ducks/contacts/selectors';
import cleanUUID from 'app/views/components/functions/cleanUUID';
import { WireInstructionParams } from 'app/models/wire-instruction-params';
import { getWireInstructions } from 'app/state/ducks/wire-instructions/selectors';
import PrimaryInput from 'app/views/components/buttons/PrimaryInput';
import { WireInstructionsModal } from './entity-wire-instructions/modal-wire-instructions';
import DeleteConfirmation from 'app/views/components/delete-confirmation/DeleteConfirmation';
import HistoryForm from './history/history';

/**
 * What the menu itself needs of an entity: its id and status. The details page
 * passes the whole entity; the grid passes its row.
 */
type EntityActionsRow = Pick<EntityParams, 'id'> & { status?: string };

type Props = {
  row: EntityActionsRow;
  withRedirection?: boolean;
  extended?: boolean;
  primaryInput?: boolean;
  /**
   * The grid's row is not the whole entity, and the grid page loads no lists:
   * Edit fetches the entity and the lists its form reads when it opens. The
   * details page has both already and leaves this off.
   */
  fetchOnOpen?: boolean;
};

export default function EntityActions({
  row,
  withRedirection,
  extended,
  primaryInput,
  fetchOnOpen,
}: Props) {
  const [entity, setEntity] = useState<EntityParams | undefined>(
    fetchOnOpen ? undefined : (row as EntityParams)
  );
  const [loadingEntity, setLoadingEntity] = useState(false);
  const [addContact, setAddContact] = useState<boolean>(false);
  const [addPaymentInstructions, setAddPaymentInstructions] =
    useState<boolean>(false);
  const entities: EntityParams[] = useSelector(getEntities);
  const contacts: ContactParams[] = useSelector(getContacts);
  const wireInstructions: WireInstructionParams[] = useSelector(getWireInstructions);
  const wireInstructionsForEntity = wireInstructions.filter(
    instruction => cleanUUID(instruction.entity.id) == entity?.id
  );
  const contactsForEntity = contacts?.filter(
    p => cleanUUID(p?.entity?.id) === entity?.id
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openEntity, setOpenEntity] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false); // Modal visibility state
  const [openHistory, setOpenHistory] = useState(false)


  useEffect(() => {
    if (!fetchOnOpen) setEntity(row as EntityParams);
  }, [row, fetchOnOpen]);

  async function editEntity(entityId: string) {
    if (!fetchOnOpen) {
      setEntity(entities.find(f => f.id == entityId));
      setOpenEntity(true);
      return;
    }
    if (loadingEntity) return;
    // The form opens behind its loader: it builds its dropdowns once, on
    // mount, from the lists in the store.
    setEntity(undefined);
    setOpenEntity(true);
    setLoadingEntity(true);
    // Without the entity the form would open empty — as Create, and a save
    // would make a NEW entity — so any failure closes it instead.
    const notLoaded = () => {
      setOpenEntity(false);
      ErrorNotification({
        title: 'Could not open entity',
        message: 'The entity could not be loaded. Try again.',
      });
    };
    try {
      const [loaded]: any[] = await Promise.all([
        dispatch(loadOneEntity(entityId, row.status ?? 'Active') as any),
        dispatch(loadEntityEditLists() as any),
      ]);
      // GET_ENTITY on success; `{ success: false, payload: <error> }` on failure
      if (!loaded?.payload?.id || loaded.success === false) {
        notLoaded();
        return;
      }
      setEntity(loaded.payload);
    } catch {
      // the fetch's own error handler throws on an answer with no response
      // (a network error or a timeout)
      notLoaded();
    } finally {
      setLoadingEntity(false);
    }
  }

  async function activate(): Promise<void> {
    const result = await activeEntity(row, dispatch);
    if (result && withRedirection) {
      navigate(`/entitymanagement/entity/${row.id}/Active`);
    }
  }

  async function deactivate(): Promise<void> {
    const result = await makeInactiveEntity(row, dispatch);
    if (result && withRedirection) {
      navigate(`/entitymanagement/entity/${row.id}/Inactive`);
    }
  }

  async function removeEntity(): Promise<void> {
    const result = await deleteEntity(row, dispatch);
    if (result && withRedirection) {
      navigate(`/entitymanagement`);
    }
  }

  function displayHistory(): any {
    setOpenHistory(true)
  }

  return (
    <div>
      {openEntity && (
        <MultiPartForm
          setOpen={setOpenEntity}
          open={openEntity}
          entityToEdit={entity}
          contactsToEdit={contactsForEntity}
          wireInstructionsToEdit={wireInstructionsForEntity}
          withRedirection={withRedirection}
          loading={loadingEntity}
        />
      )}
      <ContactsModal open={addContact} setOpen={setAddContact} entityId={row?.id} />
      <WireInstructionsModal
        setOpen={setAddPaymentInstructions}
        open={addPaymentInstructions}
        entityId={row?.id}
      />
      <Menu shadow="md" width={primaryInput ? '250px' : 200} position="bottom" withinPortal>
        <Menu.Target>
          <div style={{ width: 'fit-content' }}>
            {primaryInput ? (
              <PrimaryInput w="159px">
                Actions
                <IonIcon name="chevron-down-outline" className="primaryInputChevronDown" />
              </PrimaryInput>
            ) : (
              <PrimaryDropDown>Actions</PrimaryDropDown>
            )}
          </div>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Item
            icon={<IonIcon className="action-ion-icon" name="swap-horizontal-outline" />}
            onClick={() => editEntity(row.id as string)}
          >
            Edit
          </Menu.Item>

          {row?.status?.toLowerCase() == 'inactive' ? (
            <Menu.Item
              icon={<IonIcon className="action-ion-icon" name="swap-horizontal-outline" />}
              onClick={() => activate()}
            >
              Activate
            </Menu.Item>
          ) : (
            <Menu.Item
              icon={<IonIcon className="action-ion-icon" name="swap-horizontal-outline" />}
              onClick={() => deactivate()}
            >
              Deactivate
            </Menu.Item>
          )}
          {extended ? (
            <>
              <Menu.Item
                icon={<IonIcon className="action-ion-icon" name="swap-horizontal-outline" />}
                onClick={() => setAddContact(true)}
              >
                Add Contact
              </Menu.Item>
              <Menu.Item
                icon={<IonIcon className="action-ion-icon" name="swap-horizontal-outline" />}
                onClick={() => setAddPaymentInstructions(true)}
              >
                Add Wire Instruction
              </Menu.Item>
            </>
          ) : null}

          {row?.status?.toLowerCase() == 'inactive' ? (
            <Menu.Item
              color="red"
              icon={<IonIcon className="action-ion-icon" name="trash-outline" />}
              onClick={() => setShowDeleteModal(true)} // Show modal
            >
              Delete
            </Menu.Item>
          ) : null}
          <Menu.Item
            onClick={() => displayHistory()}
            icon={<IonIcon className="action-ion-icon" name='arrow-undo-circle-outline' />}
          >
            History
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <Modal
        size="90%"
        opened={openHistory}
        onClose={() => setOpenHistory(false)}
      >
        {openHistory && (
          <HistoryForm
            entityId={row.id ?? ''}
            close={() => setOpenHistory(false)}
            status={row.status ?? 'Active'}
          />
        )}
      </Modal>

      {/* Render Delete Confirmation Modal */}
      {showDeleteModal && (
        <DeleteConfirmation
          message='Delete This Entity?'
          onConfirm={() => {
            removeEntity();
            setShowDeleteModal(false);
          }}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
}
